import { useState, useEffect } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { AdminRecordModal, AdminModalFooter } from "../../components/admin/AdminRecordModal";
import { toast } from "sonner";
import {
  Plus,
  Package,
  PencilSimple,
  Trash,
  Bell,
  Translate,
  Envelope,
  Star,
  CreditCard,
  CalendarBlank,
  Infinity,
  Lightning,
  ArrowsClockwise,
  CheckCircle,
  XCircle,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ICON_OPTIONS = [
  { value: "bell", label: "Bell", icon: Bell },
  { value: "translate", label: "Translate", icon: Translate },
  { value: "envelope", label: "Envelope", icon: Envelope },
  { value: "star", label: "Star", icon: Star },
  { value: "credit-card", label: "Credit Card", icon: CreditCard },
  { value: "calendar", label: "Calendar", icon: CalendarBlank },
  { value: "infinity", label: "Infinity", icon: Infinity },
  { value: "lightning", label: "Lightning", icon: Lightning },
  { value: "package", label: "Package", icon: Package },
];

const getIconComponent = (iconName) => {
  const iconOption = ICON_OPTIONS.find((i) => i.value === iconName);
  return iconOption ? iconOption.icon : Package;
};

export default function AdminFeatures() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    price_monthly: 0,
    price_yearly: 0,
    feature_type: "addon",
    config: {},
    icon: "package",
    sort_order: 0,
    job_limit: null,
    photo_limit: null,
    is_active: true,
    // Metadata fields for Learn More modal
    metadata_tagline: "",
    metadata_what_it_does: "",
    metadata_why_it_matters: "",
    metadata_example_workflow: "",
  });

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      const response = await fetch(`${API}/admin/features`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setFeatures(data.features);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load features");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    try {
      const url = editingFeature
        ? `${API}/admin/features/${editingFeature.feature_id}`
        : `${API}/admin/features`;
      const method = editingFeature ? "PUT" : "POST";

      // Build metadata object ONLY with fields that have actual content
      // This ensures empty metadata never overwrites existing metadata
      const metadataPayload = {
        ...(formData.metadata_tagline?.trim() && { tagline: formData.metadata_tagline.trim() }),
        ...(formData.metadata_what_it_does?.trim() && { 
          what_it_does: formData.metadata_what_it_does.split("\n").map(s => s.trim()).filter(Boolean) 
        }),
        ...(formData.metadata_why_it_matters?.trim() && { 
          why_it_matters: formData.metadata_why_it_matters.split("\n").map(s => s.trim()).filter(Boolean) 
        }),
        ...(formData.metadata_example_workflow?.trim() && { example_workflow: formData.metadata_example_workflow.trim() }),
      };

      // Prepare data - convert empty strings to null for limits
      const submitData = {
        name: formData.name,
        slug: formData.slug,
        description: formData.description,
        price_monthly: formData.price_monthly,
        price_yearly: formData.price_yearly,
        feature_type: formData.feature_type,
        config: formData.config,
        icon: formData.icon,
        sort_order: formData.sort_order,
        is_active: formData.is_active,
        job_limit: formData.job_limit === "" || formData.job_limit === null ? null : parseInt(formData.job_limit),
        photo_limit: formData.photo_limit === "" || formData.photo_limit === null ? null : parseInt(formData.photo_limit),
      };

      // Only attach metadata if it contains at least one populated field
      if (Object.keys(metadataPayload).length > 0) {
        submitData.metadata = metadataPayload;
      }

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        toast.success(editingFeature ? "Feature updated" : "Feature created");
        setIsDialogOpen(false);
        resetForm();
        fetchFeatures();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to save feature");
      }
    } catch (error) {
      console.error('[AdminFeatures] Submit error:', error);
      toast.error("Failed to save feature");
    }
  };

  const toggleActive = async (feature) => {
    try {
      const response = await fetch(`${API}/admin/features/${feature.feature_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_active: !feature.is_active }),
      });

      if (response.ok) {
        toast.success(
          feature.is_active ? "Feature deactivated" : "Feature activated"
        );
        fetchFeatures();
      } else {
        toast.error("Failed to update feature status");
      }
    } catch (error) {
      toast.error("Failed to update feature status");
    }
  };

  const deleteFeature = async (featureId) => {
    if (!window.confirm("Delete this feature? This cannot be undone.")) return;
    try {
      await fetch(`${API}/admin/features/${featureId}`, {
        method: "DELETE",
        credentials: "include",
      });
      toast.success("Feature deleted");
      fetchFeatures();
    } catch (error) {
      toast.error("Failed to delete feature");
    }
  };

  const editFeature = (feature) => {
    setEditingFeature(feature);
    // Extract metadata fields if they exist
    const metadata = feature.metadata || {};
    setFormData({
      name: feature.name || "",
      slug: feature.slug || "",
      description: feature.description || "",
      price_monthly: feature.price_monthly || 0,
      price_yearly: feature.price_yearly || 0,
      feature_type: feature.feature_type || "addon",
      config: feature.config || {},
      icon: feature.icon || "package",
      sort_order: feature.sort_order || 0,
      job_limit: feature.job_limit,
      photo_limit: feature.photo_limit,
      is_active: feature.is_active !== false,
      // Metadata fields - convert arrays to newline-separated strings for editing
      metadata_tagline: metadata.tagline || "",
      metadata_what_it_does: Array.isArray(metadata.what_it_does) ? metadata.what_it_does.join("\n") : "",
      metadata_why_it_matters: Array.isArray(metadata.why_it_matters) ? metadata.why_it_matters.join("\n") : "",
      metadata_example_workflow: metadata.example_workflow || "",
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingFeature(null);
    setFormData({
      name: "",
      slug: "",
      description: "",
      price_monthly: 0,
      price_yearly: 0,
      feature_type: "addon",
      config: {},
      icon: "package",
      sort_order: 0,
      job_limit: null,
      photo_limit: null,
      is_active: true,
      metadata_tagline: "",
      metadata_what_it_does: "",
      metadata_why_it_matters: "",
      metadata_example_workflow: "",
    });
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
  };

  return (
    <div className="space-y-6" data-testid="admin-features">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white uppercase">
            Features
          </h1>
          <p className="text-zinc-400 mt-1">
            Manage purchasable features and their pricing
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={fetchFeatures}
            className="border-zinc-700 text-white"
          >
            <ArrowsClockwise size={18} className="mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => setIsDialogOpen(true)}
            className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
          >
            <Plus size={18} className="mr-2" />
            Add Feature
          </Button>
          <AdminRecordModal
            open={isDialogOpen}
            onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}
            title={`${editingFeature ? "Edit" : "Create"} Feature`}
            size="md"
            footer={
              <AdminModalFooter onCancel={() => { setIsDialogOpen(false); resetForm(); }}>
                <Button
                  onClick={handleSubmit}
                  className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                >
                  {editingFeature ? "Update" : "Create"}
                </Button>
              </AdminModalFooter>
            }
          >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-zinc-300">Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setFormData({
                        ...formData,
                        name,
                        slug: editingFeature ? formData.slug : generateSlug(name),
                      });
                    }}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-zinc-300">Slug</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({ ...formData, slug: e.target.value })
                    }
                    className="bg-zinc-800 border-zinc-700 text-white"
                    required
                    disabled={!!editingFeature}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-zinc-300">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="bg-zinc-800 border-zinc-700 text-white"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-zinc-300">Monthly Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price_monthly}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        price_monthly: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-zinc-300">Yearly Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price_yearly}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        price_yearly: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-zinc-300">Type</Label>
                  <Select
                    value={formData.feature_type}
                    onValueChange={(v) =>
                      setFormData({ ...formData, feature_type: v })
                    }
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="addon">Add-on</SelectItem>
                      <SelectItem value="limit_increase">Limit Increase</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-zinc-300">Icon</Label>
                  <Select
                    value={formData.icon}
                    onValueChange={(v) => setFormData({ ...formData, icon: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ICON_OPTIONS.map((icon) => {
                        const IconComp = icon.icon;
                        return (
                          <SelectItem key={icon.value} value={icon.value}>
                            <div className="flex items-center gap-2">
                              <IconComp size={16} />
                              {icon.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-zinc-300">Sort Order</Label>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sort_order: parseInt(e.target.value) || 0,
                    })
                  }
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              {/* Limit fields for Unlimited Usage type features */}
              {formData.feature_type === "limit_increase" && (
                <div className="border border-zinc-700 rounded-lg p-4 space-y-4 bg-zinc-800/30">
                  <h4 className="text-sm font-medium text-zinc-300">
                    Usage Limits (for Unlimited Usage feature)
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-zinc-400">Job Limit</Label>
                      <Input
                        type="number"
                        value={formData.job_limit ?? ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            job_limit: e.target.value === "" ? null : parseInt(e.target.value),
                          })
                        }
                        placeholder="-1 for unlimited"
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                      <p className="text-xs text-zinc-500">
                        Use -1 for unlimited, empty for no override
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-zinc-400">Photo Limit</Label>
                      <Input
                        type="number"
                        value={formData.photo_limit ?? ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            photo_limit: e.target.value === "" ? null : parseInt(e.target.value),
                          })
                        }
                        placeholder="-1 for unlimited"
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                      <p className="text-xs text-zinc-500">
                        Use -1 for unlimited, empty for no override
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
                <div>
                  <Label className="text-sm font-medium text-zinc-300">Active</Label>
                  <p className="text-xs text-zinc-500">
                    Inactive features are hidden from purchase
                  </p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
              </div>

              {/* Learn More Modal Metadata */}
              <div className="space-y-4 pt-4 border-t border-zinc-700/50">
                <h4 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider">
                  Learn More Modal Content
                </h4>
                <p className="text-xs text-zinc-500 -mt-2">
                  Optional content shown when users click "Learn More" on the upgrades page
                </p>

                <div className="space-y-2">
                  <Label htmlFor="metadata_tagline" className="text-sm text-zinc-300">Tagline</Label>
                  <Input
                    id="metadata_tagline"
                    value={formData.metadata_tagline}
                    onChange={(e) => setFormData({ ...formData, metadata_tagline: e.target.value })}
                    placeholder="Short compelling description"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="metadata_what_it_does" className="text-sm text-zinc-300">What It Does</Label>
                  <Textarea
                    id="metadata_what_it_does"
                    value={formData.metadata_what_it_does}
                    onChange={(e) => setFormData({ ...formData, metadata_what_it_does: e.target.value })}
                    placeholder="One benefit per line&#10;e.g. Sends automatic reminders&#10;Reduces no-shows by 50%"
                    rows={3}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <p className="text-xs text-zinc-500">One item per line</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="metadata_why_it_matters" className="text-sm text-zinc-300">Why It Matters</Label>
                  <Textarea
                    id="metadata_why_it_matters"
                    value={formData.metadata_why_it_matters}
                    onChange={(e) => setFormData({ ...formData, metadata_why_it_matters: e.target.value })}
                    placeholder="One benefit per line&#10;e.g. Save time on manual follow-ups&#10;Increase customer satisfaction"
                    rows={3}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <p className="text-xs text-zinc-500">One item per line</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="metadata_example_workflow" className="text-sm text-zinc-300">Example Workflow</Label>
                  <Textarea
                    id="metadata_example_workflow"
                    value={formData.metadata_example_workflow}
                    onChange={(e) => setFormData({ ...formData, metadata_example_workflow: e.target.value })}
                    placeholder="Step-by-step example of how the feature works..."
                    rows={4}
                    className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
                  />
                </div>
              </div>
          </AdminRecordModal>
        </div>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-48 bg-zinc-800 rounded-sm animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature) => {
            const IconComp = getIconComponent(feature.icon);
            return (
              <Card
                key={feature.feature_id}
                className={`bg-zinc-900 border-zinc-800 ${
                  !feature.is_active ? "opacity-60" : ""
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 flex items-center justify-center rounded-sm ${
                          feature.is_active
                            ? "bg-[#FF6600]/20 text-[#FF6600]"
                            : "bg-zinc-700 text-zinc-500"
                        }`}
                      >
                        <IconComp size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-white">{feature.name}</p>
                        <p className="text-xs text-zinc-500">{feature.slug}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleActive(feature)}
                      className="transition-colors"
                    >
                      {feature.is_active ? (
                        <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 cursor-pointer hover:bg-emerald-500/30">
                          <CheckCircle size={12} className="mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge className="bg-zinc-700 text-zinc-400 border border-zinc-600 cursor-pointer hover:bg-zinc-600">
                          <XCircle size={12} className="mr-1" />
                          Inactive
                        </Badge>
                      )}
                    </button>
                  </div>

                  <p className="text-sm text-zinc-400 mb-3 line-clamp-2">
                    {feature.description}
                  </p>

                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="border-zinc-700 text-zinc-300">
                      {feature.feature_type}
                    </Badge>
                    {feature.job_limit === -1 && (
                      <Badge variant="outline" className="border-emerald-700 text-emerald-400">
                        Unlimited Jobs
                      </Badge>
                    )}
                    {feature.photo_limit === -1 && (
                      <Badge variant="outline" className="border-emerald-700 text-emerald-400">
                        Unlimited Photos
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
                    <div className="text-sm">
                      <span className="text-[#FF6600] font-bold">
                        ${feature.price_monthly}
                      </span>
                      <span className="text-zinc-500">/mo</span>
                      <span className="text-zinc-600 ml-2">
                        (${feature.price_yearly}/yr)
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => editFeature(feature)}
                        className="text-zinc-400 hover:text-white h-8 w-8"
                      >
                        <PencilSimple size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteFeature(feature.feature_id)}
                        className="text-red-400 hover:text-red-300 h-8 w-8"
                      >
                        <Trash size={16} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
